/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import {
  Button,
  SvgIcon,
  CasesCountAndCreate,
  ToolbarItem,
  RequestStatusUpdate,
  DockedFormFooter,
  WithDropdown,
  ObjectCases,
  NavLink,
  Modal,
  AssignedToResponsibilityIdentity as AssignedTo
} from '../'

import {
  getNodeTypeFromBase64Id
} from '../../utils'

export class RequestPropertiesBar extends React.PureComponent {
  static propTypes = {
    objectId: PropTypes.string.isRequired,  // can be either file or request
    allocatedIdentity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      client: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }),
    assignedToIdentity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired
    }),
    requestStatus: PropTypes.string,
    openCases: PropTypes.array.isRequired,
    resolvedCases: PropTypes.array.isRequired,
    onStatusUpdateClick: PropTypes.func.isRequired,
    onMarkFileAsTrashClick: PropTypes.func,
    onCreateCaseClick: PropTypes.func.isRequired,
    assignedToSave: PropTypes.func
  }

  constructor(props) {
    super(props)

    this.state = {
      reassign: false,
      showStatusChangeOptions: false
    }
  }

  closeStatusDropdownAndReset = () => (
    this.setState({ showStatusChangeOptions: false })
  )

  updateRequestStatus = (status) => {
    this.setState({ showStatusChangeOptions: false })
    this.props.onStatusUpdateClick(status)
  }

  render() {
    let nodeType = getNodeTypeFromBase64Id(this.props.objectId)
    let isFileBeingProcessed = nodeType === 'FileNode'
    let allocatedIdentity = this.props.allocatedIdentity
    let assignedToIdentity = this.props.assignedToIdentity

    return (
      <div id='toolbar' className='action-toolbar'>
        <div>
          {this.props.allocatedIdentity &&
            <AllocatedIdentity
              firstName={allocatedIdentity.firstName}
              lastName={allocatedIdentity.lastName}
              id={allocatedIdentity.id}
              isContact={allocatedIdentity.isContact}
            />
          }
        </div>
        <div>
          {assignedToIdentity !== undefined &&
            <AssignedTo
              identityId={assignedToIdentity && assignedToIdentity.id}
              firstName={assignedToIdentity && assignedToIdentity.firstName}
              lastName={assignedToIdentity && assignedToIdentity.lastName}
              responsibility='handle_requests'
              assignedToSave={identityId => {
                this.setState({ reassign: false })
                this.props.assignedToSave(identityId)
              }}
              dropdownOpen={this.state.reassign}
              closeDropdown={() => this.setState({ reassign: false })}
              onClick={() => this.setState({ reassign: true })}
            />
          }
        </div>
        <div className='slds-col_bump-left slds-grid' style={{ borderLeft: `1px solid #f1f1f2` }}>
          {isFileBeingProcessed
            ? <BinFile onMarkFileAsTrashClick={() => this.props.onMarkFileAsTrashClick(this.props.objectId)} />
            : <div className='slds-col request-status-update'>
                <RequestStatusUpdate
                  value={this.props.requestStatus.toLowerCase()}
                  dropdownOpen={this.state.showStatusChangeOptions}
                  onDropdownOpenClick={() => {
                    if (!this.props.dropdownOpen) {
                      this.setState({ showStatusChangeOptions: true })
                    }
                  }}
                  closeDropdown={this.closeStatusDropdownAndReset}
                  onUpdateClick={this.updateRequestStatus}
                  disabled={['PROCESSING', 'PROCESSED'].indexOf(this.props.requestStatus) > -1}
                />
              </div>
          }
          <CasesCountAndCreate
            numberOfOpenCases={this.props.openCases.length}
            numberOfResolvedCases={this.props.resolvedCases.length}
            onCaseCreateClick={this.props.onCreateCaseClick}
            openCasesRender={ref =>
              <Modal
                ref={ref}
                heading='Open cases'
                body={
                  <ObjectCases
                    object={this.props.objectId}
                    status='open'
                    numberOfRecordsPerPage={5}
                  />
                }
                elementId='toolbar'
              />
            }
            resolvedCasesRender={ref =>
              <Modal
                ref={ref}
                heading='Resolved cases'
                body={
                  <ObjectCases
                    object={this.props.objectId}
                    status='resolved'
                    numberOfRecordsPerPage={5}
                  />
                }
                elementId='toolbar'
              />
            }
          />
        </div>
      </div>
    )
  }
}

const AllocatedIdentity = ({ firstName, lastName, id, isContact }) => (
  firstName && (
    <ToolbarItem
      label='Allocated to'
      value={
        <div style={{ lineHeight: 'normal' }}>
          <NavLink to={isContact ? `/os/identity/${id}` : `/os/identity/${id}`}>
            {`${firstName} ${lastName}`}
          </NavLink>
        </div>
      }
      className='request-allocated-to'
    />
  )
)

class BinFile extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      confirm: false
    }
  }

  render() {
    return (
      <WithDropdown
        dropdownContent={
          <div>
            <div className='slds-m-around_x-small'>
              Are you sure you want to mark file as trash?
            </div>
            <DockedFormFooter>
              <Button minWidth={55} onClick={() => this.setState({ confirm: false })}>
                No
              </Button>
              <Button minWidth={55} onClick={this.props.onMarkFileAsTrashClick}>
                Yes
              </Button>
            </DockedFormFooter>
          </div>
        }
        open={this.state.confirm}
        minWidth={120}
        minHeight={150}>
        <div
          className='slds-align_absolute-center slds-p-around_medium bin-file'
          title='Move file to trash'>
          <Button type='reset' onClick={() => this.setState({ confirm: true })}>
            <SvgIcon
              variant='utility'
              symbol='delete'
              colour={this.state.confirm ? 'light' : 'default'}
            />
          </Button>
        </div>
      </WithDropdown>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/request-properties-bar/index.js